'use client';

import { useState, memo, useCallback, useMemo, useEffect } from 'react';
import { Play } from 'lucide-react';
import toast from 'react-hot-toast';

import { AuctionStatus } from '@/components/auction/AuctionStatus';
import { CurrentPlayerSection } from '@/components/auction/CurrentPlayerSection';
import { BiddingPanel } from '@/components/auction/BiddingPanel';
import { BidHistory } from '@/components/auction/BidHistory';
import { LoadingState } from '@/components/common/LoadingState';
import { PoolSelector } from '@/components/pool/PoolSelector';
import { TeamPurseOverview } from '@/components/team/TeamPurseOverview';
import { Tabs } from '@/components/common/Tabs';
import { PlayerStatusNotification } from '@/components/auction/PlayerStatusNotification';
import { Logo } from '@/components/common/Logo';
import Link from 'next/link';
import { ShareButton } from '@/components/common/ShareButton';

import { useAuction } from '@/hooks/useAuction';
import { useSocket } from '@/hooks/useSocket';
import { useAuth } from '@/hooks/useAuth';
import { Team, Tournament } from '@/lib/types/auction';
import { SOCKET_EVENTS } from '@/lib/constants/socket';
import { tournamentService } from '@/services/api/tournaments';
import ButtonLink from '@/components/links/ButtonLink';
import { Card } from '@/components/common/Card';

interface AuctionRoomProps {
  tournament: Tournament;
}

export const AuctionRoom = memo(
  ({ tournament: initialTournament }: AuctionRoomProps) => {
    const { socket } = useSocket();
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState(0);
    const [tournament, setTournament] = useState(initialTournament);
    const [currentUrl, setCurrentUrl] = useState('');
    const tournamentId = tournament?.id;

    // Handle window.location safely for hydration
    useEffect(() => {
      if (typeof window !== 'undefined') {
        setCurrentUrl(window.location.origin);
      }
    }, []);

    const team: Team | undefined = useMemo(
      () =>
        tournament?.teams?.find(
          (team: Team) =>
            team?.ownerId === user?.id || team.owner?.id === user?.id
        ),
      [tournament?.teams, user]
    );

    const isAdmin = useMemo(
      () => tournament?.owner?.id === user?.id,
      [tournament, user]
    );
    const isTeamOwner = useMemo(() => !!team, [team]);
    const isPublicViewer = !user;

    const {
      room,
      bids,
      placeBid,
      playerStatusInfo,
      clearPlayerStatusInfo,
      handleSkipPlayer,
    } = useAuction({
      tournamentId: tournamentId || '',
      team,
      tournament, // Pass the tournament object so useAuction can check status
    });

    const currentPlayer = room?.currentPlayer;
    const currentBidAmount =
      room?.currentBid?.amount || currentPlayer?.basePrice || 0;
    const currentBidderTeam = room?.currentBid?.team.name || null;
    const currentBidderLogo = room?.currentBid?.team.logo || null;

    const handleApproveSale = useCallback(() => {
      if (currentPlayer && socket) {
        socket.emit(SOCKET_EVENTS.APPROVE_SALE, {
          playerId: currentPlayer.id,
          bidId: room?.currentBid?.id,
          tournamentId,
        });
      }
    }, [currentPlayer, socket, tournamentId, room?.currentBid?.id]);

    useEffect(() => {
      if (!socket) return;

      const handlePoolCompleted = async (data: {
        status: number;
        result: { message: string; poolId: string };
      }) => {
        if (data.status !== 1) return;

        try {
          const updatedTournament = await tournamentService.getById(
            tournamentId || ''
          );
          setTournament(updatedTournament);

          if (isAdmin) {
            toast.success(data.result.message);
          }
        } catch (err) {
          console.error('Error updating tournament data:', err);
        }
      };

      socket.on(SOCKET_EVENTS.POOL_COMPLETED, handlePoolCompleted);
      return () => {
        socket.off(SOCKET_EVENTS.POOL_COMPLETED, handlePoolCompleted);
      };
    }, [socket, isAdmin, tournamentId]);

    useEffect(() => {
      if (!socket) return;

      const handlePlayerSold = async (data: { status: number }) => {
        if (data.status !== 1) return;

        try {
          const updatedTournament = await tournamentService.getById(
            tournamentId || ''
          );
          setTournament(updatedTournament);
        } catch (err) {
          console.error('Error updating tournament data:', err);
        }
      };

      socket.on(SOCKET_EVENTS.PLAYER_SOLD, handlePlayerSold);
      return () => {
        socket.off(SOCKET_EVENTS.PLAYER_SOLD, handlePlayerSold);
      };
    }, [socket, tournamentId]);

    if (!tournament || !tournamentId) {
      return (
        <div className="flex h-full min-h-[600px] items-center justify-center p-6">
          <LoadingState message="No tournament data available" />
        </div>
      );
    }

    if (tournament.status !== 'ACTIVE') {
      return (
        <div className="flex h-full min-h-[600px] items-center justify-center p-6">
          <div className="text-center">
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              Tournament Not Active
            </h3>
            <p className="mb-4 text-gray-500">
              Live auction is only available when the tournament is in ACTIVE
              status.
            </p>
            <p className="text-sm text-gray-400">
              Current status:{' '}
              <span className="font-medium">{tournament.status}</span>
            </p>
            {tournament.status === 'UPCOMING' && (
              <p className="mt-2 text-sm text-gray-400">
                The auction will begin once the tournament organizer starts it.
              </p>
            )}
          </div>
        </div>
      );
    }

    // Only show socket-dependent content if we have a room or are admin
    if (!room && !isAdmin) {
      return (
        <div className="flex h-full min-h-[600px] items-center justify-center p-6">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-primary-500"></div>
            <p className="text-gray-500">Connecting to auction room...</p>
          </div>
        </div>
      );
    }

    if (!room || (room.status === 'waiting' && !playerStatusInfo)) {
      return (
        <div className="flex h-full min-h-[600px] flex-col space-y-2 px-2">
          {isAdmin ? (
            <div className="flex-1">
              <div className="flex h-full flex-col items-center justify-center p-2">
                <Play className="h-12 w-12 text-primary-500" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  Select Pool for Auction
                </h3>
                <p className="mt-2 text-center text-sm text-gray-500">
                  Start the auction by selecting a pool
                </p>
                <div className="mt-6 w-full">
                  <PoolSelector
                    tournamentId={tournamentId}
                    pools={tournament?.pools || []}
                    onPoolSelected={() => {}}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex h-full flex-1 items-center justify-center p-6">
              <div className="text-center">
                <Play className="mx-auto h-12 w-12 text-primary-500/50" />
                <p className="mt-4 text-gray-500">
                  Waiting for auction to start...
                </p>
              </div>
            </div>
          )}
        </div>
      );
    }

    const showBiddingPanel =
      room.status === 'active' &&
      currentPlayer &&
      isTeamOwner &&
      !isPublicViewer;

    return (
      <div className="relative">
        <div className="flex flex-col space-y-4">
          {isPublicViewer && (
            <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur-sm">
              <div className="mx-auto flex h-16 w-full max-w-[1600px] items-center justify-between px-4 sm:px-6 lg:px-8">
                <Link href="/" className="flex items-center">
                  <Logo size="md" />
                </Link>
                <div className="flex items-center gap-4">
                  <ButtonLink href="/auth/login" variant="ghost">
                    Sign In
                  </ButtonLink>
                  <ButtonLink href="/auth/register" variant="primary">
                    Get Started
                  </ButtonLink>
                </div>
              </div>
            </header>
          )}

          {playerStatusInfo && (
            <PlayerStatusNotification
              player={playerStatusInfo.player}
              team={playerStatusInfo.team}
              amount={playerStatusInfo.amount}
              status={playerStatusInfo.status}
              onClose={clearPlayerStatusInfo}
              autoCloseDelay={15000}
            />
          )}

          <div className={`mx-auto w-full ${isPublicViewer ? 'px-4' : ''}`}>
            {isPublicViewer ? (
              <Card className="p-4">
                <div className="mb-2 flex flex-col gap-3 border-b p-3 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
                  {/* Left: Tournament Name */}
                  <h1 className="text-lg font-bold text-gray-900 sm:text-2xl">
                    {tournament.name}
                  </h1>

                  {/* Right: Buttons block */}
                  <div className="flex w-full justify-end gap-3 sm:w-fit sm:min-w-[300px] sm:flex-row sm:gap-5">
                    <div className="w-1/2 sm:w-auto">
                      <AuctionStatus
                        status={room?.status || 'waiting'}
                        tournamentName={tournament.name}
                      />
                    </div>
                    <div className="flex w-1/2 justify-end sm:w-auto">
                      <ShareButton
                        title={`${tournament.name} - Live Auction`}
                        description={`Join the live auction for ${tournament.name} on Auction Chase. Watch real-time bidding, team strategies, and player acquisitions in action!`}
                        url={`${currentUrl}/auction/${tournament.id}`}
                        hashtags={[
                          'AuctionChase',
                          'LiveAuction',
                          'SportsBidding',
                        ]}
                        variant="button"
                        image={`${currentUrl}/images/og.jpg`}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={`grid grid-cols-1 gap-3 ${
                    showBiddingPanel ? 'lg:grid-cols-5' : 'lg:grid-cols-2'
                  }`}
                >
                  <div
                    className={`h-full ${showBiddingPanel ? 'lg:col-span-2' : 'lg:col-span-1'}`}
                  >
                    <CurrentPlayerSection
                      currentPlayer={currentPlayer}
                      status={room?.status || 'waiting'}
                      currentBidAmount={currentBidAmount}
                      currentBidderTeam={currentBidderTeam}
                      currentBidderLogo={currentBidderLogo}
                      isAdmin={isAdmin}
                      isTeamOwner={isTeamOwner}
                      tournamentId={tournamentId}
                      onApproveSale={handleApproveSale}
                      onSkipPlayer={handleSkipPlayer}
                    />
                  </div>

                  {showBiddingPanel && (
                    <div className="flex h-full flex-col items-center lg:col-span-1">
                      <div className="h-full w-full">
                        <BiddingPanel
                          currentBid={room.currentBid}
                          currentPlayer={room.currentPlayer}
                          hasBid={!!currentBidderTeam}
                          onPlaceBid={placeBid}
                          disabled={room.status !== 'active'}
                          tournament={tournament}
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className={`flex h-full flex-col space-y-4 ${
                      showBiddingPanel ? 'lg:col-span-2' : 'lg:col-span-1'
                    }`}
                  >
                    <Tabs
                      items={[
                        {
                          label: 'Team Purse',
                          content: (
                            <TeamPurseOverview
                              teams={tournament?.teams || []}
                            />
                          ),
                        },
                        {
                          label: 'Bid History',
                          content: <BidHistory bids={bids} />,
                        },
                      ]}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                    />
                  </div>
                </div>
              </Card>
            ) : (
              <>
                <div className="mb-2 flex flex-col gap-3 border-b p-3 sm:mb-4 sm:flex-row sm:items-center sm:justify-between">
                  {/* Left: Tournament Name */}
                  <h1 className="text-lg font-bold text-gray-900 sm:text-2xl">
                    {tournament.name}
                  </h1>

                  {/* Right: Buttons block */}
                  <div className="flex w-full justify-end gap-3 sm:w-fit sm:min-w-[300px] sm:flex-row sm:gap-5">
                    <div className="w-1/2 sm:w-auto">
                      <AuctionStatus
                        status={room?.status || 'waiting'}
                        tournamentName={tournament.name}
                      />
                    </div>
                    <div className="flex w-1/2 justify-end sm:w-auto">
                      <ShareButton
                        title={`${tournament.name} - Live Auction`}
                        description={`Join the live auction for ${tournament.name} on Auction Chase. Watch real-time bidding, team strategies, and player acquisitions in action!`}
                        url={`${currentUrl}/auction/${tournament.id}`}
                        hashtags={[
                          'AuctionChase',
                          'LiveAuction',
                          'SportsBidding',
                        ]}
                        variant="button"
                        image={`${currentUrl}/images/og.jpg`}
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={`grid grid-cols-1 gap-3 ${
                    showBiddingPanel ? 'lg:grid-cols-5' : 'lg:grid-cols-2'
                  }`}
                >
                  <div
                    className={`h-full ${showBiddingPanel ? 'lg:col-span-2' : 'lg:col-span-1'}`}
                  >
                    <CurrentPlayerSection
                      currentPlayer={currentPlayer}
                      status={room?.status || 'waiting'}
                      currentBidAmount={currentBidAmount}
                      currentBidderTeam={currentBidderTeam}
                      currentBidderLogo={currentBidderLogo}
                      isAdmin={isAdmin}
                      isTeamOwner={isTeamOwner}
                      tournamentId={tournamentId}
                      onApproveSale={handleApproveSale}
                      onSkipPlayer={handleSkipPlayer}
                    />
                  </div>

                  {showBiddingPanel && (
                    <div className="flex h-full flex-col items-center lg:col-span-1">
                      <div className="h-full w-full">
                        <BiddingPanel
                          currentBid={room.currentBid}
                          currentPlayer={room.currentPlayer}
                          hasBid={!!currentBidderTeam}
                          onPlaceBid={placeBid}
                          disabled={room.status !== 'active'}
                          tournament={tournament}
                        />
                      </div>
                    </div>
                  )}

                  <div
                    className={`flex h-full flex-col space-y-4 ${
                      showBiddingPanel ? 'lg:col-span-2' : 'lg:col-span-1'
                    }`}
                  >
                    <Tabs
                      items={[
                        {
                          label: 'Team Purse',
                          content: (
                            <TeamPurseOverview
                              teams={tournament?.teams || []}
                            />
                          ),
                        },
                        {
                          label: 'Bid History',
                          content: <BidHistory bids={bids} />,
                        },
                      ]}
                      activeTab={activeTab}
                      onTabChange={setActiveTab}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);

AuctionRoom.displayName = 'AuctionRoom';
